// components/ChatInput.js
import React, { useState, useRef } from "react";
import axios from "axios";
import { Upload, X } from "lucide-react";

const ChatInput = ({
  onSubmit,
  isSubmitting,
  token,
  API_URL,
  placeholder = "Type your message...",
}) => {
  const [inputText, setInputText] = useState("");
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleSubmit = async (e) => {
    if (!inputText.trim() && !uploadedImage) return;

    onSubmit({
      text: inputText,
      imageId: uploadedImage?._id,
    });

    setInputText("");
    setUploadedImage(null);
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file is an image
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    // Validate file size (e.g., 5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert("File size should be less than 5MB");
      return;
    }

    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${API_URL}/api/files/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const fileData = response.data.file;

      // Fetch the image for preview
      const imageResponse = await axios.get(
        `${API_URL}/api/files/download/${fileData._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      const imageUrl = URL.createObjectURL(imageResponse.data);
      setUploadedImage({ ...fileData, previewUrl: imageUrl });
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Failed to upload image");
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removeImage = () => {
    if (uploadedImage?.previewUrl) {
      URL.revokeObjectURL(uploadedImage.previewUrl);
    }
    setUploadedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="fixed bottom-0 right-0 left-0 md:left-64 bg-gray-900 border-t border-gray-800 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Image Preview */}
        {uploadedImage && (
          <div className="mb-4 relative">
            <img
              src={uploadedImage.previewUrl}
              alt="Uploaded preview"
              className="max-h-48 rounded-lg object-contain bg-gray-800 p-2"
            />
            <button
              onClick={removeImage}
              className="absolute top-2 right-2 p-1 bg-gray-900/80 rounded-full hover:bg-gray-800"
            >
              <X size={20} className="text-gray-300" />
            </button>
          </div>
        )}

        {/* Input Area */}
        <div className="flex gap-2">
          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            className="flex-1 rounded-lg bg-gray-800 border-gray-700 text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            placeholder={placeholder}
            onKeyPress={(e) => e.key === "Enter" && handleSubmit()}
          />

          {/* Upload Button */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*"
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className={`px-4 py-2 rounded-lg transition-colors
              ${
                isUploading
                  ? "bg-gray-700 cursor-not-allowed"
                  : "bg-gray-600 hover:bg-gray-700"
              }`}
          >
            {isUploading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Upload size={20} />
            )}
          </button>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || (!inputText.trim() && !uploadedImage)}
            className={`px-6 py-2 rounded-lg transition-colors flex items-center gap-2
              ${
                isSubmitting || (!inputText.trim() && !uploadedImage)
                  ? "bg-blue-900/50 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Processing...
              </>
            ) : (
              "Send"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatInput;
